import { useState, useEffect, useRef, useCallback } from "react";
import { APP_REGISTRY, GROUP_ORDER, GROUP_COLORS } from "../../lib/appRegistry";
import { useAuth } from "../../context/AuthContext";
import { addToDesktop, removeFromDesktop, isOnDesktop } from "../../lib/desktopIcons";

interface TileCtx {
  id: string;
  x: number;
  y: number;
}

const RECENT_APPS_KEY = "opentool-recent-apps";
const MAX_RECENT = 3;

function getRecentApps(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_APPS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export function trackRecentApp(appId: string) {
  try {
    const recent = getRecentApps().filter((id) => id !== appId);
    recent.unshift(appId);
    localStorage.setItem(RECENT_APPS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch { /* ignore */ }
}

/** Group description subtitles */
const GROUP_DESCRIPTIONS: Record<string, string> = {
  Workstation: "Your daily-driver apps",
  "Mission Control": "Admin & governance tools",
};

interface StartMenuProps {
  onNavigate: (id: string) => void;
  onClose: () => void;
  activeWindowId: string | null;
}

export function StartMenu({ onNavigate, onClose, activeWindowId }: StartMenuProps) {
  const { user, logout } = useAuth();
  const isAdmin = !!user?.admin;
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [recentIds] = useState(() => getRecentApps());
  const [tileCtx, setTileCtx] = useState<TileCtx | null>(null);
  const [, forceUpdate] = useState(0);

  // Focus search on mount
  useEffect(() => {
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        // Don't close if clicking the start button
        const target = e.target as HTMLElement;
        if (target.closest(".wm-taskbar__start")) return;
        onClose();
      }
    };
    // Delay to avoid immediate close from the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", onClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  const handleNavigate = useCallback((id: string) => {
    trackRecentApp(id);
    onNavigate(id);
    onClose();
  }, [onNavigate, onClose]);

  const handleTileCtx = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setTileCtx({ id, x: e.clientX, y: e.clientY });
  }, []);

  const handleToggleDesktop = useCallback((id: string) => {
    if (isOnDesktop(id)) {
      removeFromDesktop(id);
    } else {
      addToDesktop(id);
    }
    window.dispatchEvent(new CustomEvent("opentool-desktop-icons-changed"));
    forceUpdate((n) => n + 1);
    setTileCtx(null);
  }, []);

  // Filter tiles — respect showInStartMenu and adminOnly
  const visibleTiles = APP_REGISTRY.filter((tile) => {
    if (tile.showInStartMenu === false) return false;
    if (tile.adminOnly && !isAdmin) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      tile.label.toLowerCase().includes(q) ||
      tile.id.toLowerCase().includes(q) ||
      tile.group.toLowerCase().includes(q)
    );
  });

  const activeTiles = visibleTiles.filter((t) => !t.comingSoon);
  const comingSoonTiles = visibleTiles.filter((t) => t.comingSoon);

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    tiles: activeTiles.filter((t) => t.group === group),
  })).filter((g) => g.tiles.length > 0);

  // Recent apps
  const recentApps = recentIds
    .map((id) => APP_REGISTRY.find((a) => a.id === id))
    .filter(Boolean)
    .filter((a) => !a!.adminOnly || isAdmin)
    .slice(0, MAX_RECENT) as typeof APP_REGISTRY[number][];

  return (
    <div className="wm-startmenu" ref={menuRef} onClick={() => tileCtx && setTileCtx(null)}>
      <div className="wm-startmenu__search">
        <svg className="wm-startmenu__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={searchRef}
          type="text"
          placeholder="Search apps..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="wm-startmenu__body">
        {/* Recent section */}
        {!search && recentApps.length > 0 && (
          <div className="wm-startmenu__group">
            <div className="wm-startmenu__group-header">
              <div className="wm-startmenu__group-label" style={{ color: "#8b8baa" }}>
                Recent
              </div>
            </div>
            <div className="wm-startmenu__recent">
              {recentApps.map((tile) => {
                const isActive = activeWindowId === tile.id;
                return (
                  <button
                    key={tile.id}
                    className={`wm-startmenu__recent-item ${isActive ? "wm-startmenu__recent-item--active" : ""}`}
                    onClick={() => handleNavigate(tile.id)}
                  >
                    <div className="wm-startmenu__recent-icon">
                      <svg viewBox="0 0 24 24">
                        <path d={tile.icon} />
                      </svg>
                    </div>
                    <span className="wm-startmenu__recent-label">{tile.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {grouped.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#6b6b9a", fontSize: 13 }}>
            No apps matching &ldquo;{search}&rdquo;
          </div>
        )}
        {grouped.map(({ group, tiles }) => (
          <div key={group} className="wm-startmenu__group">
            <div className="wm-startmenu__group-header">
              <div
                className="wm-startmenu__group-label"
                style={{ color: GROUP_COLORS[group] || "#6b7280" }}
              >
                {group}
              </div>
              {GROUP_DESCRIPTIONS[group] && (
                <div className="wm-startmenu__group-desc">
                  {GROUP_DESCRIPTIONS[group]}
                </div>
              )}
            </div>
            <div className="wm-startmenu__grid">
              {tiles.map((tile) => {
                const isActive = activeWindowId === tile.id;
                const groupColor = GROUP_COLORS[tile.group] || "#4f8fff";
                return (
                  <button
                    key={tile.id}
                    className={`wm-startmenu__tile ${isActive ? "wm-startmenu__tile--active" : ""}`}
                    onClick={() => handleNavigate(tile.id)}
                    onContextMenu={(e) => handleTileCtx(e, tile.id)}
                  >
                    <div
                      className="wm-startmenu__tile-icon"
                      style={{
                        borderColor: `${groupColor}35`,
                        boxShadow: `0 0 12px ${groupColor}30, 0 0 24px ${groupColor}10`,
                        color: groupColor,
                      }}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d={tile.icon} />
                      </svg>
                    </div>
                    <span className="wm-startmenu__tile-label">{tile.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Coming Soon section */}
        {comingSoonTiles.length > 0 && !search && (
          <div className="wm-startmenu__group">
            <div className="wm-startmenu__group-header">
              <div className="wm-startmenu__group-label" style={{ color: "#484f58" }}>
                Coming Soon
              </div>
              <div className="wm-startmenu__group-desc">In development</div>
            </div>
            <div className="wm-startmenu__grid">
              {comingSoonTiles.map((tile) => (
                <button
                  key={tile.id}
                  className="wm-startmenu__tile wm-startmenu__tile--coming-soon"
                  style={{ opacity: 0.35, cursor: "default", pointerEvents: "none" }}
                >
                  <div
                    className="wm-startmenu__tile-icon"
                    style={{ borderColor: "rgba(255,255,255,0.05)", color: "#484f58" }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d={tile.icon} />
                    </svg>
                  </div>
                  <span className="wm-startmenu__tile-label">{tile.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tile right-click context menu */}
      {tileCtx && (
        <div
          className="wm-ctx"
          style={{ position: "fixed", left: tileCtx.x, top: tileCtx.y, zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="wm-ctx__item" onClick={() => { handleNavigate(tileCtx.id); setTileCtx(null); }}>
            Open
          </button>
          <button className="wm-ctx__item" onClick={() => handleToggleDesktop(tileCtx.id)}>
            {isOnDesktop(tileCtx.id) ? "Remove from Desktop" : "Add to Desktop"}
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="wm-startmenu__footer">
        <span className="wm-startmenu__footer-version">
          <span style={{ color: '#4f8fff', fontWeight: 700 }}>&gt;</span>{' '}
          <span style={{ color: '#e6edf3', fontWeight: 700 }}>tool</span>
          <span style={{ color: '#4f8fff', fontWeight: 700 }}>_os</span>
          <span style={{ color: '#484f58', fontWeight: 400, marginLeft: 6, fontSize: '0.85em' }}>{__GIT_SHA__}</span>
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && (
            <button
              className="wm-startmenu__footer-link"
              onClick={() => handleNavigate("admin")}
            >
              Mission Control
            </button>
          )}
          <button
            className="wm-startmenu__footer-link"
            onClick={() => { onClose(); logout(); }}
            style={{ color: '#f87171' }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
